// Helpers for the "Reuse a previous design" section.
//
// `decodeReusableDesigns(cart)` walks the customer's cart looking for line
// items the customiser added (marker: `_added_by: "rk-builder"`). For each
// such line it decodes the per-spot properties back into ReusableDesign
// objects (one per spot) so the section can show each design as its own
// card in the grid.

import type { CartJson } from "./api";
import type { Arrangement, ContentMode, EmbroideryDraft } from "./sections/PlacementSection";

// Cart item titles can arrive HTML-entity encoded (e.g. "Men&#39;s").
// Decode them so the card's "From ..." label reads naturally.
function decodeEntities(s: string): string {
  if (!s || typeof document === "undefined") return s;
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
}

export type ReusableDesign = {
  // Unique key for React rendering: composed of cart line id + spot index.
  key: string;
  // Human label for the source line ("Heavyweight Hoodie · Black").
  sourceLabel: string;
  // The decoded spot fields, ready to drop into a fresh draft.
  draft: EmbroideryDraft;
};

export function decodeReusableDesigns(cart: CartJson | null): ReusableDesign[] {
  if (!cart || !Array.isArray(cart.items)) return [];
  const out: ReusableDesign[] = [];
  for (const item of cart.items) {
    const props = item.properties || {};
    if (props["_added_by"] !== "rk-builder") continue;
    const count = parseInt(props["_spot_count"] ?? "", 10);
    if (!Number.isFinite(count) || count <= 0) continue;
    const sourceLabel = decodeEntities(
      [item.title, item.variant_title].filter(Boolean).join(" · "),
    );
    for (let n = 1; n <= count; n++) {
      const draft = decodeSpot(props, n);
      if (!draft) continue;
      out.push({ key: `${item.id}-${n}`, sourceLabel, draft });
    }
  }
  return out;
}

function decodeSpot(props: Record<string, string>, n: number): EmbroideryDraft | null {
  const slug = props[`_spot_${n}_slug`];
  const mode = props[`_spot_${n}_mode`] as ContentMode | undefined;
  if (!slug || !mode) return null;
  const sizeMm = parseInt(props[`_spot_${n}_size_mm`] ?? "", 10);
  const splitCodes = (raw: string | undefined): string[] =>
    raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : [];
  return {
    placementSlug: slug,
    mode,
    text: props[`_spot_${n}_text`] ?? "",
    fontId: props[`_spot_${n}_font_id`] ?? null,
    // Word Art: round-trip the style pick so a reused Word Art spot
    // reopens with its preset instead of a bare font-less draft.
    wordArtStyleId: props[`_spot_${n}_wordart_style_id`] ?? null,
    uploadStagingKey: props[`_spot_${n}_upload_staging_key`] ?? null,
    // Phase 66: the visible "Spot N design filename" property is the
    // canonical key; fall back to the legacy hidden key for cart lines
    // made before Phase 66 (mirrors api.builder.lookup-order-spots.tsx).
    uploadFilename:
      props[`Spot ${n} design filename`] ?? props[`_Spot ${n} design filename`] ??
      props[`_spot_${n}_filename`] ??
      null,
    uploadMimeType: props[`_spot_${n}_mime_type`] ?? null,
    uploadFileHash: props[`_spot_${n}_upload_file_hash`] ?? null,
    // Upload-later: round-trip the flag so a reused full-bag line whose
    // artwork is still coming decodes as a valid "sending later" draft
    // instead of a broken incomplete one.
    uploadLater: props[`_spot_${n}_upload_later`] === "1",
    // The digitisation-memory flag is re-evaluated on the new order's
    // upload step, not carried from the source cart line. Defaults false
    // so pricing.ts charges the fee unless that lookup says otherwise.
    uploadAlreadyDigitised: false,
    arrangement: (props[`_spot_${n}_arrangement`] as Arrangement | undefined) ?? null,
    sizeMm: Number.isFinite(sizeMm) ? sizeMm : 80,
    textThreadCodes: splitCodes(props[`_spot_${n}_text_thread_codes`]),
    logoThreadCodes: splitCodes(props[`_spot_${n}_logo_thread_codes`]),
    addonUpsellId: props[`_spot_${n}_addon_id`] ?? null,
    // Phase 33b/c: pet-portrait / kid-drawing extensions. Default to
    // unset; the customer can re-tune on reuse.
    addonCount: (() => {
      const c = parseInt(props[`_spot_${n}_addon_count`] ?? "", 10);
      return Number.isFinite(c) && c > 0 ? c : null;
    })(),
    addonStyleColoured: props[`_spot_${n}_addon_style`] === "coloured",
    addonStyleSingleLine: props[`_spot_${n}_addon_style`] === "single-line",
    // Person flow faces preference - round-trips so a reused people
    // drawing keeps its faces choice.
    includeFaces: props[`_spot_${n}_faces`] === "1",
    extraAddonIds: (() => {
      const csv = props[`_spot_${n}_extra_addon_ids`] ?? "";
      return csv ? csv.split(",").map((s) => s.trim()).filter(Boolean) : [];
    })(),
    extraUploads: (() => {
      const out: EmbroideryDraft["extraUploads"] = [];
      // _spot_N_extra_M_staging_key (M is 1-indexed)
      for (let m = 1; m <= 8; m++) {
        const sk = props[`_spot_${n}_extra_${m}_staging_key`];
        if (!sk) break;
        out.push({
          stagingKey: sk,
          // Phase 66: visible "Spot N extra M filename" is canonical.
          filename:
            props[`Spot ${n} extra ${m} filename`] ?? props[`_Spot ${n} extra ${m} filename`] ??
            props[`_spot_${n}_extra_${m}_filename`] ??
            "",
          mimeType: props[`_spot_${n}_extra_${m}_mime_type`] ?? null,
          fileHash: props[`_spot_${n}_extra_${m}_file_hash`] ?? null,
        });
      }
      return out;
    })(),
    // Phase 29: only set by past-order lookup of legacy Easify orders;
    // RK-builder cart lines never carry it.
    legacyUploadUrl: null,
    // Varsity / college outline colour — carried so a reused college
    // design keeps its outline; the customer can re-pick on reuse.
    outlineThreadCode: props[`_spot_${n}_outline_thread_code`] || null,
    // Phase 67i: when this cart line was itself added via past-order
    // reuse, it carries `_spot_N_source_order` — propagate so the next
    // order keeps pointing at the original.
    sourceOrderName: props[`_spot_${n}_source_order`] || null,
  };
}
